import {
  Paper,
  List,
  Divider,
  TextField,
  InputAdornment,
  Tooltip,
  IconButton,
  Slider,
} from "@mui/material";
import { useIsStorybook } from "utils/customHooks/storyHooks";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { useIsDarkMode } from "store/atoms";
import { ListItemToggle } from "components/Settings/ListItemToggle";
import { CollapsibleConfigStyles } from "pages/DashboardPages/ProcessTilesControls/CollapsibleConfigStyles";
import styled, { css } from "styled-components/macro";
import { INITIAL_VISIBLE_TIMELINES, useTimelinesStore } from "./timelinesStore";
import { atomWithStorage } from "jotai/utils";
import { startCase } from "lodash";
import { useIsTimelineExpanded } from "components/Dashboard/HubletView/SingleTimeline/singleTimelineStore";
import { Fragment } from "react/jsx-runtime";
import { Replay, Speed, TurnRight, UnfoldMore } from "@mui/icons-material";
import { useGetPageFromPath } from "utils/customHooks/genericHooks";
import {
  ErrorOutline,
  FormatListNumbered,
  Label,
  People,
  Person,
  Troubleshoot,
} from "@mui/icons-material";
import { useAtom } from "jotai";

// These icons are used throughout the components
const SUBTIMELINE_ICONS = {
  teamSize: <People />,
  sku: <Label />,
  rejectCount: <ErrorOutline />,
  count: <FormatListNumbered />,
  operator: <Person />,
  grossRejectNetTicks: <Troubleshoot />,
  speedGainLoss: <Speed />,
};

const TimelinesConfig = ({ isSettingsPage = false }) => {
  const {
    showTimelineShiftIndicators,
    setShowTimelineShiftIndicators,
    showTimelineTooltipOnHover,
    setShowTimelineTooltipOnHover,
  } = useTimelinesStore();
  const isStorybook = useIsStorybook();
  const { isHubletPage } = useGetPageFromPath();
  const { translate } = useFlightTranslation();
  const [isDarkMode] = useIsDarkMode();

  return (
    <CollapsibleConfigStyles
      onClick={e => e.stopPropagation()}
      $isSettingsPage={isSettingsPage}
      $isDarkMode={isDarkMode}
      $customCss={css`
        position: absolute;
        right: 0;
      `}
    >
      <StyledPaper elevation={isSettingsPage ? 0 : 2}>
        <List>
          {/* showTimelineShiftIndicators control */}
          <ListItemToggle
            {...(isStorybook ? {} : { button: true })}
            onChange={() =>
              setShowTimelineShiftIndicators(!showTimelineShiftIndicators)
            }
            className={`switchWrapper${
              showTimelineShiftIndicators ? " checked" : ""
            }`}
            checked={showTimelineShiftIndicators}
            label={translate("Shift_Indicators")}
            style={{ padding: "0px 8px", height: 42 }}
            labelStyle={{ padding: "0px 8px" }}
          />
          {/* showTimelineTooltipOnHover control */}
          <ListItemToggle
            {...(isStorybook ? {} : { button: true })}
            onChange={() =>
              setShowTimelineTooltipOnHover(!showTimelineTooltipOnHover)
            }
            className={`switchWrapper${
              showTimelineTooltipOnHover ? " checked" : ""
            }`}
            checked={showTimelineTooltipOnHover}
            label={translate("Show_Tooltip_on_Hover")}
            style={{ padding: "0px 8px", height: 42 }}
            labelStyle={{ padding: "0px 8px" }}
          />
          {isHubletPage && (
            <>
              <Divider style={{ marginBottom: 8, marginTop: 4 }} />
              <TimelineDisplayControls />
            </>
          )}
        </List>
      </StyledPaper>
    </CollapsibleConfigStyles>
  );
};

export default TimelinesConfig;

const StyledPaper = styled(Paper)`
  margin-right: 12px;
`;

export const timelineHideSegmentsSmallerThanPxAtom = atomWithStorage(
  "timelineHideSegmentsSmallerThanPx",
  0
);
export const speedGainLossTargetSpeedAtom = atomWithStorage(
  "speedGainLossTargetSpeed",
  100
);
/** controls which sub-timelines are visible below SingleTimeline */
function TimelineDisplayControls() {
  const { visibleSubTimelines, setVisibleSubTimelines } = useTimelinesStore();
  const { translate } = useFlightTranslation();
  const [isTimelineExpanded, setIsTimelineExpanded] = useIsTimelineExpanded();
  const [speedGainLossTargetSpeed, setSpeedGainLossTargetSpeed] = useAtom(
    speedGainLossTargetSpeedAtom
  );
  return (
    <>
      <div className="timeline-display-controls" style={{ paddingLeft: 6 }}>
        <ListItemToggle
          onChange={() => {
            setIsTimelineExpanded(!isTimelineExpanded);
          }}
          className={`switchWrapper${isTimelineExpanded ? " checked" : ""}`}
          icon={<UnfoldMore />}
          checked={isTimelineExpanded}
          label={translate("Expand_Timeline")}
          style={{ padding: "0px 8px", height: 42 }}
          labelStyle={{ padding: "0px 8px" }}
        />
        <Divider style={{ marginBottom: 8, marginTop: 4 }} />
        {Object.entries(INITIAL_VISIBLE_TIMELINES).map(([timelineName]) => {
          const isVisible = visibleSubTimelines[timelineName];
          const label =
            TITLES[timelineName] ?? translate(startCase(timelineName));
          const isVeryLongLabel = label.length > 16;
          const isSpeedGainLoss = timelineName === "speedGainLoss";
          return (
            <Fragment key={timelineName}>
              <ListItemToggle
                onChange={() => {
                  if (!isTimelineExpanded) {
                    setIsTimelineExpanded(true);
                  }
                  const enablingSpeedGainLossButGrossNetRejectIsDisabled =
                    isSpeedGainLoss &&
                    !isVisible &&
                    !visibleSubTimelines.grossRejectNetTicks;
                  const disablingGrossNetRejectButSpeedGainLossIsEnabled =
                    isVisible &&
                    timelineName === "grossRejectNetTicks" &&
                    visibleSubTimelines.speedGainLoss;

                  setVisibleSubTimelines({
                    ...visibleSubTimelines,
                    [timelineName]: !isVisible,
                    // if we just enabled speed gain/loss timeline and gross/net/reject timeline is disabled, we must enable it (speedGainLoss depends on grossNetReject timeline)
                    ...(enablingSpeedGainLossButGrossNetRejectIsDisabled && {
                      grossRejectNetTicks: true,
                    }),
                    // if we just disabled gross/net/reject timeline and speed gain/loss timeline is enabled, we must disable it (speedGainLoss depends on grossNetReject timeline)
                    ...(disablingGrossNetRejectButSpeedGainLossIsEnabled && {
                      speedGainLoss: false,
                    }),
                  });
                }}
                className={`switchWrapper${isVisible ? " checked" : ""}`}
                checked={isVisible}
                label={label}
                icon={SUBTIMELINE_ICONS[timelineName]}
                style={{
                  padding: "0px 8px",
                  height: 42,
                  ...(isTimelineExpanded ? {} : { opacity: 0.5 }),
                  ...(isVeryLongLabel ? { fontSize: 15 } : {}),
                }}
                labelStyle={{ padding: "0px 8px" }}
              />
              {isSpeedGainLoss && (
                <Tooltip title="Threshold at which (+/-10%) speed gain/loss colors change">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      margin: "0 0 0 12px",
                      opacity:
                        isTimelineExpanded && visibleSubTimelines.speedGainLoss
                          ? 1
                          : 0.5,
                    }}
                  >
                    {/* number input for speed gain/loss "Threshold" starting at 100% */}
                    <TurnRight style={{ transform: "scaleY(-1)" }} />
                    <Slider
                      min={0}
                      max={200}
                      value={speedGainLossTargetSpeed}
                      onChange={(_, value) =>
                        setSpeedGainLossTargetSpeed(value as number)
                      }
                      style={{ width: 128, margin: "0 16px" }}
                    />
                    <TextField
                      label="Threshold"
                      size="small"
                      style={{ width: 128 }}
                      type="number"
                      value={speedGainLossTargetSpeed}
                      onChange={e =>
                        setSpeedGainLossTargetSpeed(Number(e.target.value))
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">%</InputAdornment>
                        ),
                        style: { fontSize: 12 },
                      }}
                    />
                    <IconButton
                      disabled={speedGainLossTargetSpeed === 100}
                      onClick={() => setSpeedGainLossTargetSpeed(100)}
                    >
                      <Replay />
                    </IconButton>
                  </div>
                </Tooltip>
              )}
            </Fragment>
          );
        })}
      </div>
    </>
  );
}

const TITLES = {
  grossRejectNetTicks: "Gross/Reject/Net Ticks",
  speedGainLoss: "Speed Gain/Loss",
  count: "Net Count",
};
